import React from "react";

import { Checkbox } from "@mui/material";

import {
    StatusDotSmall,
    StatusPill,
} from "components/organisms/GenericTable/GenericTable.styled";
import { GenericTableColumn } from "components/organisms/GenericTable/GenericTable.types";

import NotificationCell from "./NotificationCell";
import NotificationCellMenu from "./NotificationCellMenu";

export type NotificationLevel = 1 | 2 | 3 | 4;

export const LEVEL_META: Record<
    NotificationLevel,
    { label: string; status: "attention" | "idle" | "healthy" }
> = {
    1: { label: "Info", status: "idle" },
    2: { label: "Warning", status: "attention" },
    3: { label: "Alert", status: "attention" },
    4: { label: "Message", status: "idle" },
};

export const getLevelMeta = (level?: number | null) => {
    if (level === 1 || level === 2 || level === 3 || level === 4) {
        return LEVEL_META[level];
    }
    return { label: "—", status: "idle" as const };
};

type CreateColumnsArgs = {
    selectedIds: Array<string | number>;
    pageIds: Array<string | number>;
    onToggleRow: (id: string | number) => void;
    onToggleAll: () => void;
    onMarkAsRead: (id: string | number) => void;
    onRequestDelete: (id: string | number) => void;
    deletingId?: string | number | null;
    markingId?: string | number | null;
    actionsDisabled?: boolean;
};

export const createNotificationsListColumns = ({
    selectedIds,
    pageIds,
    onToggleRow,
    onToggleAll,
    onMarkAsRead,
    onRequestDelete,
    deletingId = null,
    markingId = null,
    actionsDisabled = false,
}: CreateColumnsArgs): GenericTableColumn[] => {
    const allSelected =
        pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));
    const someSelected =
        pageIds.some((id) => selectedIds.includes(id)) && !allSelected;

    return [
        {
            key: "select",
            header: (
                <Checkbox
                    size="small"
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={onToggleAll}
                    onClick={(event) => event.stopPropagation()}
                    inputProps={{
                        "aria-label": "Select all notifications on page",
                    }}
                    disabled={pageIds.length === 0 || actionsDisabled}
                />
            ),
            label: "Select",
            fixedWidth: 48,
            hideable: false,
            hideInCardView: true,
            align: "center",
            render: (row) => (
                <Checkbox
                    size="small"
                    checked={selectedIds.includes(row.id)}
                    onChange={() => onToggleRow(row.id)}
                    onClick={(event) => event.stopPropagation()}
                    inputProps={{
                        "aria-label": `Select notification ${row.id}`,
                    }}
                    disabled={actionsDisabled}
                />
            ),
        },
        {
            key: "notification",
            header: "Content",
            label: "Content",
            width: 3.5,
            minWidth: 220,
            render: (row) => <NotificationCell notification={row} />,
        },
        {
            key: "level",
            header: "Level",
            label: "Level",
            fixedWidth: 120,
            render: (row) => {
                const meta = getLevelMeta(row.level);
                return (
                    <StatusPill $status={meta.status}>
                        <StatusDotSmall />
                        {meta.label}
                    </StatusPill>
                );
            },
        },
        {
            key: "date",
            header: "Date",
            label: "Date",
            fixedWidth: 160,
            render: (row) => row.created_at || "—",
        },
        {
            key: "status",
            header: "Status",
            label: "Status",
            fixedWidth: 120,
            render: (row) => {
                const isRead = !!row.read_at;
                return (
                    <StatusPill $status={isRead ? "healthy" : "attention"}>
                        <StatusDotSmall />
                        {isRead ? "Read" : "Unread"}
                    </StatusPill>
                );
            },
        },
        {
            key: "actions",
            header: "",
            label: "Actions",
            align: "right",
            hideable: false,
            fixedWidth: 120,
            cardActions: true,
            render: (row) => (
                <NotificationCellMenu
                    notification={row}
                    onMarkAsRead={onMarkAsRead}
                    onRequestDelete={onRequestDelete}
                    isDeleting={deletingId === row.id}
                    isMarking={markingId === row.id}
                    disabled={actionsDisabled}
                />
            ),
        },
    ];
};
